import { t } from "./i18n-platform-shared";
import { getSemanticEnumTagColor, type SemanticEnumRegistry } from "./semantic-enum";

export type PlatformStatus = "online" | "degraded" | "offline" | "unknown";

export type PlatformStatusTagColor = "success" | "warning" | "error" | "default";

const PLATFORM_STATUS_VALUES = ["online", "degraded", "offline", "unknown"] as const satisfies readonly PlatformStatus[];

const PLATFORM_STATUS_PRESENTATION_REGISTRY = {
  online: { tone: "success" },
  degraded: { tone: "warning" },
  offline: { tone: "error" },
  unknown: { tone: "default" },
} as const satisfies SemanticEnumRegistry<PlatformStatus>;

function readPlatformStatus(value: PlatformStatus | string | null | undefined): PlatformStatus | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return PLATFORM_STATUS_VALUES.includes(trimmed as PlatformStatus) ? (trimmed as PlatformStatus) : null;
}

export function getPlatformStatusLabel(status: PlatformStatus): string {
  return t(`platform.statuses.${status}`);
}

export function getPlatformStatusTagColor(status: PlatformStatus | string | null | undefined): PlatformStatusTagColor {
  return getSemanticEnumTagColor(PLATFORM_STATUS_PRESENTATION_REGISTRY, readPlatformStatus(status), "warning") as PlatformStatusTagColor;
}

export type AuthMode = "local" | "hybrid" | "sso";

export function getAuthModeLabel(mode: AuthMode | string | null | undefined): string {
  if (mode === "local") return t("common.auth.localLogin");
  if (mode === "hybrid") return t("common.auth.hybridLogin");
  if (mode === "sso") return t("common.auth.ssoLogin");
  return mode ? String(mode) : "-";
}

export function getAssetProviderLabel(providerType: string | null | undefined): string {
  if (providerType === "local") return t("platform.shared.values.providerTypeLocal");
  if (providerType === "center") return t("platform.shared.values.providerTypeCenter");
  return providerType ? String(providerType) : "-";
}

export function getExecutionProviderLabel(providerType: string | null | undefined): string {
  if (providerType === "local") return t("platform.shared.values.executionProviderTypeLocal");
  if (providerType === "center") return t("platform.shared.values.executionProviderTypeCenter");
  return providerType ? String(providerType) : "-";
}

export type PlatformProviderDomain = "identity" | "asset" | "execution" | "business";

export function getPlatformProviderPhaseLabel(
  phase: string | null | undefined,
  domain: PlatformProviderDomain = "business",
): string {
  if (!phase) return "-";
  if (phase === "target") {
    if (domain === "identity") return t("platform.shared.values.providerPhaseTargetIdentity");
    if (domain === "asset") return t("platform.shared.values.providerPhaseTargetAsset");
    if (domain === "execution") return t("platform.shared.values.providerPhaseTargetExecution");
    return t("platform.shared.values.providerPhaseTargetBusiness");
  }
  if (phase === "transition") return t("platform.shared.values.providerPhaseTransition");
  if (phase === "legacy") return t("platform.shared.values.providerPhaseLegacy");
  if (phase === "disabled") return t("platform.shared.values.providerPhaseDisabled");
  return String(phase);
}
